import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {Editor} from 'grapesjs';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import type {EmailImageSourceInfo} from '@EmailMakerReact/lib/email-maker-admin-tools-library';
import {getEmailImageSources, selectComponentById} from '@EmailMakerReact/lib/email-maker-admin-tools-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import {useIsMounted} from '@Hooks/useIsMounted';
import {ADMIN_IMAGE_SOURCES_EMPTY, ADMIN_IMAGE_SOURCES_LOADING, ADMIN_IMAGE_SOURCES_SOURCE_PREFIX} from '../../email-maker-admin-image-sources-modal.types';
import styles from './image-sources-grid.module.scss';

export interface ImageSourcesGridProps {
  editor: Editor;
  onComponentSelected: () => void;
}

export function ImageSourcesGrid({editor, onComponentSelected}: ImageSourcesGridProps): ReactElement {
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(true);
  const [sources, setSources] = useState<EmailImageSourceInfo[]>([]);

  useEffect((): void => {
    setIsLoading(true);
    getEmailImageSources(editor)
      .then((result) => {
        if (isMounted.current) {
          setSources(result);
          setIsLoading(false);
        }
      })
      .catch((e: unknown) => {
        logEmailMakerError('ImageSourcesGrid: failed to load image sources', e);
        if (isMounted.current) {
          setSources([]);
          setIsLoading(false);
        }
      });
  }, [editor, isMounted]);

  function handleSelect(componentId: string): void {
    if (selectComponentById(editor, componentId)) {
      onComponentSelected();
    }
  }

  if (isLoading) {
    return <Text val={ADMIN_IMAGE_SOURCES_LOADING} size={TextSize.MD} className={styles.message} />;
  }
  if (sources.length === 0) {
    return <Text val={ADMIN_IMAGE_SOURCES_EMPTY} size={TextSize.MD} className={styles.message} />;
  }

  return (
    <div className={styles.grid}>
      {sources.map((info) => (
        <ClickableDiv
          key={info.componentId}
          className={styles.item}
          onClick={(): void => {
            handleSelect(info.componentId);
          }}
        >
          <img alt={info.source} className={styles.image} src={info.url} />
          <Text val={`${ADMIN_IMAGE_SOURCES_SOURCE_PREFIX}${info.source}`} size={TextSize.SM} bold className={styles.source} />
        </ClickableDiv>
      ))}
    </div>
  );
}
